import { EditableFieldProps } from './editable-field-json-props'

// Renamed to EditableFieldProps in editable-field-json-props.ts.
export type { EditableFieldProps as BoundFieldProps } from './editable-field-json-props'

export const IS_REQUIRED_UI_ERROR = 'is required'

// Fallback error text when a rejected field has no reason recorded.
export const IS_REJECTED_UI_ERROR = 'value was rejected'

/**
 * Minimal shape of an `acceptRejectOverrideField` value the shells need
 * to compute rejection state. `ReviewValue` in ui2-review-field-props
 * satisfies this interface and adds the `reviewedBy` edge.
 */
export interface IRejectionValue {
  status?: 'accepted' | 'rejected' | 'overridden'
  reason?: string
  originalValue?: any
}

interface IFilterUiErrorsOptions {
  isRequired?: boolean
  isReadOnly?: boolean
  isDisabled?: boolean
}

export function filterUiErrors(
  errors: any[] | undefined,
  options: IFilterUiErrorsOptions,
): any[] | undefined {
  if (!errors || errors.length === 0) return errors
  const { isRequired, isReadOnly, isDisabled } = options
  if (isRequired && !isReadOnly && !isDisabled) return errors
  const isRequiredMessage = (candidate: any): boolean => {
    if (typeof candidate === 'string') return candidate === IS_REQUIRED_UI_ERROR
    if (Array.isArray(candidate)) return candidate.every(isRequiredMessage)
    const message = candidate?.detail || candidate?.message
    return message === IS_REQUIRED_UI_ERROR
  }
  return errors.filter((entry) => !isRequiredMessage(entry))
}

/**
 * Shared "is this bound field required but currently empty?" predicate.
 *
 * Callers:
 *   - `mergeUIStateErrors` (entity/renderer) — decides which paths get the
 *     synthetic `IS_REQUIRED_UI_ERROR` in the merged errors map.
 *   - The renderer's per-field reporter — tells sections to auto-expand
 *     when they contain a required-empty descendant.
 *   - `UI2FieldShell.getEffectiveErrors` (web + mobile) — paints red when
 *     the parent form section requests it (e.g. after a wizard Next block).
 *
 * The predicate itself is one line — extracted so all three consumers agree
 * on which fields count as unfilled and honor the same custom-emptiness
 * hook. Callers pass whichever custom emptiness fn they have available:
 *   - ui2 shells pass `props.isValueEmpty`.
 *   - The renderer passes an adapter over the component class's
 *     `static isValueEmptyForRequired(value, props)`.
 */
export interface IIsRequiredEmptyArgs {
  isRequired?: boolean
  isReadOnly?: boolean
  isDisabled?: boolean
  /** Legacy `disabled` prop from the form-field paradigm — treated as `isDisabled`. */
  disabled?: boolean
  /** Legacy `isEditable !== false` gate from the form-field paradigm. */
  isEditable?: boolean
  value: unknown
  isValueEmpty?: (value: unknown) => boolean
}

export function isRequiredEmpty(args: IIsRequiredEmptyArgs): boolean {
  const { isRequired, isReadOnly, isDisabled, disabled, isEditable, value, isValueEmpty } = args
  if (!isRequired) return false
  if (isReadOnly || isDisabled || disabled) return false
  if (isEditable === false) return false
  return isValueEmpty ? isValueEmpty(value) : isValueDefaultEmpty(value)
}

function isValueDefaultEmpty(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

// ---------------------------------------------------------------------------
// Runtime state for bound fields
// ---------------------------------------------------------------------------

/**
 * Runtime state used by fields that support inheritable disabled/read-only state.
 */
export interface InheritableBoundFieldState {
  isDisabled: boolean;
  isReadOnly: boolean;
}

/**
 * Runtime state used by fields that support prefilled mode.
 */
export interface PrefilledFieldState<T> {
  /**
   * The value that existed when the field entered prefilled mode.
   * Used to restore the value if editing is cancelled.
   */
  originalValue: T | undefined;

  /**
   * Whether the field is currently being edited.
   */
  isEditing: boolean;
}

/**
 * Resolves the final field state by combining the props passed to the field
 * with any inherited state from a parent component.
 */
export function resolveInheritedFieldState(
  props: EditableFieldProps,
  inheritedState?: Partial<InheritableBoundFieldState>
): InheritableBoundFieldState {
  return {
    isDisabled:
      inheritedState?.isDisabled === true ? true
        : props.isDisabled ?? false,

    isReadOnly:
      inheritedState?.isReadOnly === true ? true
        : props.isReadOnly ?? false,
  };
}

/**
 * Determines whether a value should be treated as populated.
 */
export function hasFieldValue(value: unknown): boolean {
  return value !== undefined &&
    value !== null &&
    value !== "" &&
    !(Array.isArray(value) && value.length === 0);
}

/**
 * Creates the initial prefilled state for a field.
 */
export function createPrefilledFieldState<T>(
  value: T | undefined,
  enablePrefilledMode: boolean
): PrefilledFieldState<T> {
  const shouldStartInDisplayMode =
    enablePrefilledMode && hasFieldValue(value);

  return {
    originalValue: shouldStartInDisplayMode ? value : undefined,
    isEditing: !shouldStartInDisplayMode,
  };
}

/**
 * Transitions a prefilled field into edit mode.
 */
export function beginPrefilledEdit<T>(
  state: PrefilledFieldState<T>
): PrefilledFieldState<T> {
  return { ...state, isEditing: true };
}

/**
 * Transitions a prefilled field out of edit mode.
 */
export function cancelPrefilledEdit<T>(
  state: PrefilledFieldState<T>
): PrefilledFieldState<T> {
  return { ...state, isEditing: false };
}
